import { useMemo, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'

import type { PriceHistoryPointDto } from '../api/marketAnalytics'
import { formatAnalyticsDateShort, formatAnalyticsDateTime, formatAnalyticsRub } from '../screens/marketAnalyticsFormat'

type PriceHistoryChartProps = {
  points: PriceHistoryPointDto[]
  loading?: boolean
  error?: string | null
}

/** Не больше стольки подписей на оси — иначе на длинной истории они наедут друг на друга. */
const MAX_AXIS_LABELS = 5

/** Индексы точек для подписи оси: всегда первая и последняя, остальные — равномерно между ними. */
const pickAxisIndices = (count: number, max: number): number[] => {
  if (count <= max) {
    return Array.from({ length: count }, (_, index) => index)
  }
  const step = (count - 1) / (max - 1)
  const indices = new Set<number>()
  for (let i = 0; i < max; i++) {
    indices.add(Math.round(i * step))
  }
  return Array.from(indices).sort((a, b) => a - b)
}

export const PriceHistoryChart = ({ points, loading = false, error = null }: PriceHistoryChartProps) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  const chart = useMemo(() => {
    if (points.length === 0) {
      return null
    }
    const width = 520
    const height = 168
    const padX = 12
    const padTop = 16
    const padBottom = 34
    const prices = points.map((point) => point.price)
    const min = Math.min(...prices)
    const max = Math.max(...prices)
    const span = max - min || Math.max(max * 0.05, 1)
    const innerW = width - padX * 2
    const innerH = height - padTop - padBottom
    const coords = points.map((point, index) => {
      const x =
        points.length === 1
          ? padX + innerW / 2
          : padX + (index / (points.length - 1)) * innerW
      const y = padTop + innerH - ((point.price - min) / span) * innerH
      return { x, y, point }
    })
    const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(' ')
    const first = points[0]
    const last = points[points.length - 1]
    const axisIndices = pickAxisIndices(points.length, MAX_AXIS_LABELS)
    return { width, height, padX, padTop, padBottom, path, coords, min, max, first, last, axisIndices }
  }, [points])

  const handlePointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!chart || chart.coords.length === 0 || !svgRef.current) {
      return
    }
    const rect = svgRef.current.getBoundingClientRect()
    if (rect.width === 0) {
      return
    }
    const relativeX = ((event.clientX - rect.left) / rect.width) * chart.width
    let nearest = 0
    let nearestDistance = Infinity
    chart.coords.forEach((coord, index) => {
      const distance = Math.abs(coord.x - relativeX)
      if (distance < nearestDistance) {
        nearestDistance = distance
        nearest = index
      }
    })
    setHoverIndex(nearest)
  }

  const handlePointerLeave = () => setHoverIndex(null)

  if (loading) {
    return <p className="echat-muted market-analytics-history__status">Загрузка графика…</p>
  }
  if (error) {
    return (
      <p className="echat-error market-analytics-history__status" role="alert">
        {error}
      </p>
    )
  }
  if (!chart) {
    return <p className="echat-muted market-analytics-history__status">Нет точек истории по этой позиции.</p>
  }

  const hovered = hoverIndex != null ? chart.coords[hoverIndex] : null
  const tooltipWidth = 122
  const tooltipHeight = 40
  const tooltipX = hovered
    ? Math.min(Math.max(hovered.x - tooltipWidth / 2, 4), chart.width - tooltipWidth - 4)
    : 0
  const tooltipAbove = hovered ? hovered.y - chart.padTop >= tooltipHeight + 8 : true
  const tooltipY = hovered ? (tooltipAbove ? hovered.y - tooltipHeight - 10 : hovered.y + 10) : 0
  const plotMidY = chart.padTop + (chart.height - chart.padTop - chart.padBottom) / 2
  const axisLabelY = chart.height - chart.padBottom + 16

  return (
    <div className="market-analytics-history">
      <div className="market-analytics-history__meta">
        <span>
          {formatAnalyticsRub(chart.first.price)} → {formatAnalyticsRub(chart.last.price)}
        </span>
        <span className="echat-muted">
          {formatAnalyticsDateTime(chart.first.observed_at)} —{' '}
          {formatAnalyticsDateTime(chart.last.observed_at)} · {points.length} точ.
        </span>
      </div>
      <svg
        ref={svgRef}
        className="market-analytics-history__svg"
        viewBox={`0 0 ${chart.width} ${chart.height}`}
        role="img"
        aria-label={`График изменения цены: ${formatAnalyticsRub(chart.first.price)} → ${formatAnalyticsRub(chart.last.price)}`}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      >
        <line
          className="market-analytics-history__grid"
          x1={chart.padX}
          x2={chart.width - chart.padX}
          y1={plotMidY}
          y2={plotMidY}
        />

        {chart.axisIndices.map((index) => {
          const coord = chart.coords[index]
          const anchor = index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'
          return (
            <text
              key={`axis-${coord.point.observed_at}-${index}`}
              className="market-analytics-history__axis-label"
              x={coord.x}
              y={axisLabelY}
              textAnchor={anchor}
            >
              {formatAnalyticsDateShort(coord.point.observed_at)}
            </text>
          )
        })}

        <path className="market-analytics-history__line" d={chart.path} />

        {chart.coords.map((coord, index) => (
          <circle
            key={`${coord.point.observed_at}-${coord.point.price}`}
            className={
              hoverIndex === index
                ? 'market-analytics-history__dot market-analytics-history__dot--active'
                : 'market-analytics-history__dot'
            }
            cx={coord.x}
            cy={coord.y}
            r={hoverIndex === index ? 5 : 3.2}
          />
        ))}

        {hovered ? (
          <g className="market-analytics-history__hover" pointerEvents="none">
            <line
              className="market-analytics-history__hover-line"
              x1={hovered.x}
              x2={hovered.x}
              y1={chart.padTop}
              y2={chart.height - chart.padBottom}
            />
            <rect
              className="market-analytics-history__tooltip-bg"
              x={tooltipX}
              y={tooltipY}
              width={tooltipWidth}
              height={tooltipHeight}
              rx={6}
            />
            <text
              className="market-analytics-history__tooltip-price"
              x={tooltipX + tooltipWidth / 2}
              y={tooltipY + 17}
              textAnchor="middle"
            >
              {formatAnalyticsRub(hovered.point.price)}
            </text>
            <text
              className="market-analytics-history__tooltip-date"
              x={tooltipX + tooltipWidth / 2}
              y={tooltipY + 31}
              textAnchor="middle"
            >
              {formatAnalyticsDateTime(hovered.point.observed_at)}
            </text>
          </g>
        ) : null}
      </svg>
      <div className="market-analytics-history__scale echat-muted">
        <span>min {formatAnalyticsRub(chart.min)}</span>
        <span>max {formatAnalyticsRub(chart.max)}</span>
      </div>
    </div>
  )
}
